(() => {
  'use strict';

  // Max companion: the normal mouse pointer always stays visible (so every link works as usual).
  // Max only appears, floating next to the pointer, after the mouse rests for a moment,
  // and never on top of links, buttons or text boxes. Moving the mouse hides him again.

  const IMAGE_URL = 'https://cdn.shopify.com/s/files/1/0741/7744/6049/files/max-flying-cursor-approved-transparent-v4.png?v=1784729848';
  const ROOT_ID = 'ary-max-cursor-v4';
  const DOT_CLASS = 'ary-max-dot-v4';
  const CURSOR_WIDTH = 72;
  const CURSOR_HEIGHT = 79;
  const IDLE_MS = 650;
  const INTERACTIVE = 'a[href],button,input,select,textarea,label,summary,option,[role="button"],[role="link"],[role="tab"],[role="menuitem"],[role="option"],[role="checkbox"],[role="radio"],[role="switch"],[onclick],[contenteditable="true"],[tabindex]:not([tabindex="-1"])';

  const html = document.documentElement;

  if (typeof window.__aryMaxCursorV4Destroy === 'function') {
    window.__aryMaxCursorV4Destroy();
  }
  if (typeof window.__aryMaxCursorDestroy === 'function') {
    window.__aryMaxCursorDestroy();
  }

  const oldSelectors = [
    '#ary-max-cursor', '#max-cursor', '#max-follower', '#flying-max-cursor',
    '.max-cursor', '.max-follower', '.flying-max-cursor', '.cursor-character',
    '.cursor-candy', '.cursor-trail', '.max-trail', '.ary-cursor-trail',
    '.ary-max-trail', '.ary-max-particle', '.ary-max-candy',
    '.ary-max-falling-dot', '.ary-max-dot'
  ];

  function purgeOldCursorElements() {
    document.querySelectorAll(oldSelectors.join(',')).forEach((element) => element.remove());

    document.querySelectorAll('img').forEach((image) => {
      if (image.closest(`#${ROOT_ID}`)) return;

      const source = image.currentSrc || image.src || '';
      if (!/(max[-_ ]?(cursor|flying|follower)|ary[-_ ]?max[-_ ]?(cursor|flying)|flying[-_ ]?max)/i.test(source)) return;

      const style = getComputedStyle(image);
      const wrapper = image.closest('[id*="cursor" i],[class*="cursor" i],[id*="follower" i],[class*="follower" i]');
      if (wrapper || style.position === 'fixed') {
        (wrapper || image).remove();
      }
    });
  }

  purgeOldCursorElements();
  document.getElementById(ROOT_ID)?.remove();
  document.querySelectorAll(`.${DOT_CLASS}`).forEach((element) => element.remove());
  html.classList.remove('ary-max-active', 'ary-max-cursor-on', 'ary-max-v4-on', 'ary-max-v4-clickable');

  const disabled = matchMedia('(pointer: coarse)').matches ||
    innerWidth < 750 ||
    matchMedia('(prefers-reduced-motion: reduce)').matches;

  if (disabled) return;

  const cursor = document.createElement('div');
  cursor.id = ROOT_ID;
  cursor.setAttribute('aria-hidden', 'true');

  const character = document.createElement('span');
  character.className = 'ary-max-body-v4';

  const image = document.createElement('img');
  image.src = IMAGE_URL;
  image.alt = '';
  image.draggable = false;
  image.style.setProperty('--ary-max-facing', '1');

  character.appendChild(image);
  cursor.appendChild(character);
  document.body.appendChild(cursor);

  let mouseX = -500;
  let mouseY = -500;
  let inside = false;
  let visible = false;
  let lastMoveAt = performance.now();
  let appearAt = 0;
  let frame = 0;
  let cleanupTimer = 0;

  const dotColors = ['#ff4f9a', '#ffd447', '#63d8ff', '#9c7cff', '#77e38f', '#ff8f45'];

  function addDot(x, y) {
    const dot = document.createElement('i');
    dot.className = DOT_CLASS;
    const size = 2.5 + Math.random() * 3.5;
    dot.style.left = `${x}px`;
    dot.style.top = `${y}px`;
    dot.style.setProperty('--dot-size', `${size}px`);
    dot.style.setProperty('--dot-color', dotColors[Math.floor(Math.random() * dotColors.length)]);
    dot.style.setProperty('--dot-drift', `${-13 + Math.random() * 26}px`);
    dot.style.setProperty('--dot-fall', `${30 + Math.random() * 30}px`);
    dot.style.setProperty('--dot-time', `${620 + Math.random() * 330}ms`);
    document.body.appendChild(dot);
    setTimeout(() => dot.remove(), 1100);
  }

  function isInteractive(element) {
    if (!element || element.nodeType !== 1) return true;
    if (element.closest(INTERACTIVE)) return true;
    const pointer = (getComputedStyle(element).cursor || '').split(',').pop().trim();
    return pointer !== '' && pointer !== 'auto' && pointer !== 'default';
  }

  function hide() {
    if (!visible) return;
    visible = false;
    cursor.classList.remove('is-visible');
  }

  function show(now) {
    let left = mouseX + 16;
    let top = mouseY + 2;
    let facing = 1;
    if (left + CURSOR_WIDTH > innerWidth - 6) {
      left = mouseX - 16 - CURSOR_WIDTH;
      facing = -1;
    }
    if (top + CURSOR_HEIGHT > innerHeight - 6) top = innerHeight - 6 - CURSOR_HEIGHT;
    if (top < 6) top = 6;
    image.style.setProperty('--ary-max-facing', String(facing));
    cursor.style.transform = `translate3d(${left}px, ${top}px, 0)`;
    visible = true;
    appearAt = now;
    cursor.classList.add('is-visible');
    for (let i = 0; i < 4; i += 1) {
      addDot(left + 14 + Math.random() * (CURSOR_WIDTH - 28), top + 22 + Math.random() * 30);
    }
  }

  function wake() {
    lastMoveAt = performance.now();
    hide();
  }

  function onMouseMove(event) {
    mouseX = event.clientX;
    mouseY = event.clientY;
    inside = true;
    wake();
  }

  function onMouseLeave() {
    inside = false;
    hide();
  }

  function animate(now) {
    if (!visible && inside && now - lastMoveAt > IDLE_MS && !document.hidden) {
      const under = document.elementFromPoint(mouseX, mouseY);
      if (!isInteractive(under)) show(now);
      else lastMoveAt = now;
    }

    if (visible) {
      const t = Math.min(1, (now - appearAt) / 320);
      const pop = 0.6 + 0.4 * (1 - Math.pow(1 - t, 3));
      const bobX = Math.sin(now * 0.0021) * 1.4;
      const bobY = Math.sin(now * 0.0030) * 3.1;
      const tilt = Math.sin(now * 0.0017) * 1.8;
      character.style.transform = `translate3d(${bobX}px, ${bobY}px, 0) rotate(${tilt}deg) scale(${pop})`;
    }

    frame = requestAnimationFrame(animate);
  }

  function destroy() {
    cancelAnimationFrame(frame);
    clearInterval(cleanupTimer);
    observer.disconnect();
    document.removeEventListener('mousemove', onMouseMove);
    document.removeEventListener('mouseleave', onMouseLeave);
    document.removeEventListener('mousedown', wake);
    document.removeEventListener('keydown', wake);
    window.removeEventListener('scroll', wake);
    window.removeEventListener('wheel', wake);
    window.removeEventListener('blur', onMouseLeave);
    cursor.remove();
    document.querySelectorAll(`.${DOT_CLASS}`).forEach((element) => element.remove());
    delete window.__aryMaxCursorV4Destroy;
  }

  image.addEventListener('error', destroy, { once: true });
  document.addEventListener('mousemove', onMouseMove, { passive: true });
  document.addEventListener('mouseleave', onMouseLeave, { passive: true });
  document.addEventListener('mousedown', wake, { passive: true });
  document.addEventListener('keydown', wake, { passive: true });
  window.addEventListener('scroll', wake, { passive: true });
  window.addEventListener('wheel', wake, { passive: true });
  window.addEventListener('blur', onMouseLeave);

  const observer = new MutationObserver(() => purgeOldCursorElements());
  observer.observe(document.body, { childList: true, subtree: true });

  cleanupTimer = setInterval(purgeOldCursorElements, 700);
  setTimeout(() => clearInterval(cleanupTimer), 12000);

  frame = requestAnimationFrame(animate);
  window.__aryMaxCursorV4Destroy = destroy;
})();
